<script setup>
// SiteUp (Fase 4): gerencia os materiais do send_material. O admin sobe o
// arquivo (hospedado na nossa infra via ActiveStorage/MinIO) com rótulo e
// legenda; a IA usa a `key` do material pra enviar na conversa.
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';

const route = useRoute();
const store = useStore();
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const assistantId = computed(() => Number(route.params.assistantId));
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);

const materials = ref([]);
watch(
  assistant,
  a => {
    const list = a?.config?.materials;
    if (Array.isArray(list)) materials.value = [...list];
  },
  { immediate: true }
);

const fileInput = ref(null);
const selectedFile = ref(null);
const label = ref('');
const caption = ref('');
const uploading = ref(false);

const backUrl = computed(() => ({
  name: 'captain_assistants_settings_index',
  params: {
    accountId: route.params.accountId,
    assistantId: assistantId.value,
  },
}));

const canAdd = computed(() => !!selectedFile.value && !uploading.value);

const onFileChange = e => {
  selectedFile.value = e.target.files?.[0] || null;
  if (selectedFile.value && !label.value) {
    label.value = selectedFile.value.name.replace(/\.[^/.]+$/, '');
  }
};

const addMaterial = async () => {
  if (!selectedFile.value) return;
  uploading.value = true;
  try {
    const form = new FormData();
    form.append('attachment', selectedFile.value);
    form.append('label', label.value || '');
    form.append('caption', caption.value || '');
    const { data } = await CaptainAssistantAPI.uploadMaterial(
      assistantId.value,
      form
    );
    materials.value = [...materials.value, data];
    selectedFile.value = null;
    label.value = '';
    caption.value = '';
    if (fileInput.value) fileInput.value.value = '';
    useAlert('Material adicionado e hospedado.');
  } catch (error) {
    useAlert('Não foi possível enviar o material.');
  } finally {
    uploading.value = false;
  }
};

const removeMaterial = async key => {
  try {
    await CaptainAssistantAPI.removeMaterial(assistantId.value, key);
    materials.value = materials.value.filter(m => m.key !== key);
    useAlert('Material removido.');
  } catch (error) {
    useAlert('Não foi possível remover o material.');
  }
};
</script>

<template>
  <PageLayout
    header-title="Materiais"
    :is-fetching="isFetching"
    :back-url="backUrl"
    :show-know-more="false"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
  >
    <template #body>
      <SettingsHeader
        heading="Materiais"
        description="Arquivos que o agente pode enviar na conversa (prova social, demonstrativo, contrato). Suba aqui — fica hospedado na nossa infra e a IA usa a chave do material."
      />

      <div
        class="flex flex-col gap-3 mt-7 p-4 rounded-xl border border-n-weak bg-n-alpha-1"
      >
        <input
          ref="fileInput"
          type="file"
          class="text-sm text-n-slate-12 file:mr-3 file:rounded-md file:border-0 file:bg-n-brand file:px-3 file:py-1.5 file:text-white file:text-sm"
          @change="onFileChange"
        />
        <Input v-model="label" placeholder="Rótulo (ex.: Prova social)" />
        <Input v-model="caption" placeholder="Legenda enviada junto (opcional)" />
        <div class="flex justify-end">
          <Button
            :label="uploading ? 'Enviando...' : 'Adicionar material'"
            :disabled="!canAdd"
            @click="addMaterial"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2 mt-6">
        <div v-if="materials.length === 0" class="text-sm text-n-slate-11">
          Nenhum material ainda. Suba o primeiro acima.
        </div>
        <div
          v-for="m in materials"
          :key="m.key"
          class="flex items-center justify-between gap-3 p-3 rounded-xl border border-n-weak"
        >
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span
                :class="m.type === 'image' ? 'i-ri-image-line' : 'i-ri-file-line'"
                class="size-4 text-n-slate-11 flex-shrink-0"
              />
              <span class="text-sm font-medium text-n-slate-12 truncate">{{ m.label }}</span>
              <code class="text-xs text-n-slate-11 flex-shrink-0">{{ m.key }}</code>
            </div>
            <p v-if="m.caption" class="text-xs text-n-slate-11 mt-0.5 truncate">
              {{ m.caption }}
            </p>
            <a
              :href="m.file_url"
              target="_blank"
              rel="noopener"
              class="text-xs text-n-brand hover:underline"
            >ver arquivo</a>
          </div>
          <Button
            label="Remover"
            color="ruby"
            ghost
            xs
            class="flex-shrink-0"
            @click="removeMaterial(m.key)"
          />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
